import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  motion, 
  useScroll, 
  useTransform, 
  useSpring,
  useMotionValue,
  AnimatePresence
} from 'framer-motion';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaArrowRight,
  FaCheck,
  FaTimes,
  FaChevronDown,
  FaRocket,
  FaSpinner,
  FaPaperPlane
} from 'react-icons/fa';

// Modern Hero Section with 3D Elements
const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: 
    linear-gradient(135deg, #ffffff 0%, #f8f9ff 30%, #ffffff 70%, #f5f7ff 100%),
    radial-gradient(circle at 20% 80%, rgba(0, 122, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 59, 48, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(48, 209, 88, 0.02) 0%, transparent 50%);
  perspective: 1000px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(rgba(0, 122, 255, 0.02) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 122, 255, 0.02) 1px, transparent 1px);
    background-size: 60px 60px;
    opacity: 0.5;
    animation: gridMove 20s linear infinite, gridPulse 8s ease-in-out infinite;
  }
  
  @keyframes gridMove {
    0% { transform: translate(0, 0) rotate(0deg); }
    25% { transform: translate(30px, 15px) rotate(1deg); }
    50% { transform: translate(60px, 60px) rotate(0deg); }
    75% { transform: translate(30px, 45px) rotate(-1deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
  }
  
  @keyframes gridPulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 0.8; }
  }
`;

const HeroBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 30% 70%, rgba(0, 122, 255, 0.08) 0%, transparent 40%),
    radial-gradient(circle at 70% 30%, rgba(255, 59, 48, 0.05) 0%, transparent 40%),
    radial-gradient(circle at 20% 20%, rgba(48, 209, 88, 0.04) 0%, transparent 40%);
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const FloatingShape = styled(motion.div)`
  position: absolute;
  background: ${props => props.gradient};
  border-radius: ${props => props.borderRadius || '50%'};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
`;

const GlassMorphShape = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.radius || '20px'};
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  text-align: center;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(3rem, 7vw, 7rem);
  font-weight: 900;
  margin-bottom: ${props => props.theme.spacing.lg};
  background: linear-gradient(135deg, 
    #1a1a1a 0%, 
    #007AFF 25%, 
    #5856D6 50%, 
    #FF3B30 75%, 
    #1a1a1a 100%);
  background-size: 400% 400%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientFlow 8s ease infinite;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  @keyframes gradientFlow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: rgba(26, 26, 26, 0.8);
  margin-bottom: ${props => props.theme.spacing.xxl};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: ${props => props.theme.spacing.xxl};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: rgba(26, 26, 26, 0.6);
  cursor: pointer;
  
  svg {
    font-size: 1.5rem;
    animation: floatDown 2s ease-in-out infinite;
  }
  
  @keyframes floatDown {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(10px); }
  }
  
  &:hover {
    color: #007AFF;
  }
`;

// Contact Section with Advanced Design
const ContactSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const ContactContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: ${props => props.theme.spacing.xxxl};
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xxl};
  }
`;

// Contact Info with 3D Cards
const ContactInfo = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.radius.xlarge};
  padding: ${props => props.theme.spacing.xxl};
  position: relative;
  overflow: hidden;
  transform-style: preserve-3d;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #007AFF, #5856D6, #FF3B30);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const ContactDetail = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0, 122, 255, 0.1), transparent);
    transition: left 0.5s;
  }
  
  &:hover:before {
    left: 100%;
  }
`;

const ContactIcon = styled(motion.div)`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, ${props => props.color1 || '#007AFF'}, ${props => props.color2 || '#5856D6'});
  border-radius: ${props => props.theme.radius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    background: linear-gradient(135deg, ${props => props.color1 || '#007AFF'}, ${props => props.color2 || '#5856D6'});
    border-radius: ${props => props.theme.radius.medium};
    z-index: -1;
    filter: blur(15px);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;

const ContactText = styled.div`
  flex: 1;
`;

const ContactLabel = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ContactValue = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 0.95rem;
`;

// Advanced Contact Form
const ContactForm = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.radius.xlarge};
  padding: ${props => props.theme.spacing.xxl};
  position: relative;
  overflow: hidden;
  transform-style: preserve-3d;
`;

const FormTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.text};
  background: linear-gradient(135deg, #007AFF, #5856D6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled(motion.div)`
  position: relative;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FormInput = styled(motion.input)`
  width: 100%;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.lg};
  border: 2px solid rgba(0, 122, 255, 0.1);
  border-radius: ${props => props.theme.radius.medium};
  background: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  outline: none;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(255, 255, 255, 1);
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textLight};
    transition: opacity 0.3s ease;
  }
  
  &:focus::placeholder {
    opacity: 0.7;
  }
`;

const FormTextarea = styled(motion.textarea)`
  width: 100%;
  padding: ${props => props.theme.spacing.lg};
  border: 2px solid rgba(0, 122, 255, 0.1);
  border-radius: ${props => props.theme.radius.medium};
  background: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  outline: none;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  font-family: inherit;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(255, 255, 255, 1);
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textLight};
    transition: opacity 0.3s ease;
  }
  
  &:focus::placeholder {
    opacity: 0.7;
  }
`;

const FormLabel = styled(motion.label)`
  position: absolute;
  top: ${props => props.theme.spacing.lg};
  left: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.textLight};
  font-size: 1rem;
  pointer-events: none;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.95);
  padding: 0 ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.radius.small};
  
  ${FormInput}:focus + &,
  ${FormInput}:not(:placeholder-shown) + &,
  ${FormTextarea}:focus + &,
  ${FormTextarea}:not(:placeholder-shown) + & {
    top: -${props => props.theme.spacing.xs};
    left: ${props => props.theme.spacing.md};
    font-size: 0.85rem;
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    background: rgba(255, 255, 255, 1);
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xxl};
  border: none;
  border-radius: ${props => props.theme.radius.medium};
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  box-shadow: ${props => props.theme.shadows.large};
  position: relative;
  overflow: hidden;
  width: 100%;
  justify-content: center;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover:before {
    left: 100%;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
`;

const StatusMessage = styled(motion.div)`
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.radius.medium};
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-weight: 600;
  
  ${props => props.type === 'success' && `
    background: rgba(48, 209, 88, 0.1);
    color: #30D158;
    border: 1px solid rgba(48, 209, 88, 0.2);
  `}
  
  ${props => props.type === 'error' && `
    background: rgba(255, 59, 48, 0.1);
    color: #FF3B30;
    border: 1px solid rgba(255, 59, 48, 0.2);
  `}
`;

const Contact = ({ setCursorVariant }) => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    document.title = "Contattaci | D-Soft";
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formcarry.com/s/SWEMlwAmH4w', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Hero Section */}
      <HeroSection ref={heroRef}>
        <HeroBackground style={{ y: parallaxY, opacity }} />
        
        <FloatingElements>
          {/* Glass Morphism Shapes */}
          <GlassMorphShape
            radius="30px"
            animate={{
              x: [0, 100, -50, 80, 0],
              y: [0, -80, 40, -60, 0],
              rotate: [0, 180, 360, 540, 720],
              scale: [1, 1.2, 0.8, 1.1, 1],
              borderRadius: ["30px", "50px", "20px", "40px", "30px"],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '200px',
              height: '200px',
              top: '10%',
              left: '10%',
            }}
          />
          
          <GlassMorphShape
            radius="50%"
            animate={{
              x: [0, -120, 80, -60, 0],
              y: [0, 100, -60, 80, 0],
              scale: [1, 1.2, 0.8, 1.3, 1],
              rotate: [0, -180, -360, -540, -720],
              skewX: [0, 10, -5, 8, 0],
              skewY: [0, -5, 10, -3, 0],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '150px',
              height: '150px',
              top: '70%',
              right: '15%',
            }}
          />

          {/* Colored Glass Elements */}
          {[...Array(4)].map((_, i) => (
            <FloatingShape
              key={i}
              gradient={`linear-gradient(135deg, 
                rgba(${['0, 122, 255', '255, 59, 48', '48, 209, 88', '255, 204, 0'][i]}, 0.1) 0%, 
                rgba(${['88, 86, 214', '255, 149, 0', '50, 215, 75', '255, 214, 10'][i]}, 0.05) 100%)`}
              borderRadius={i % 2 === 0 ? '50%' : '20px'}
              animate={{
                x: [0, 80 + i * 20, -40 - i * 10, 60 + i * 15, 0],
                y: [0, -60 - i * 15, 80 + i * 10, -40 - i * 8, 0],
                rotate: [0, 120 + i * 30, 240 + i * 60, 360 + i * 90, 720],
                scale: [1, 1.1 + i * 0.1, 0.9 - i * 0.05, 1.2 + i * 0.08, 1],
                borderRadius: i % 2 === 0 ? 
                  ["50%", "30%", "60%", "40%", "50%"] : 
                  ["20px", "40px", "10px", "30px", "20px"],
              }}
              transition={{
                duration: 20 + i * 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.5,
              }}
              style={{
                width: `${100 + i * 30}px`,
                height: `${100 + i * 30}px`,
                top: `${15 + i * 20}%`,
                left: `${10 + i * 20}%`,
              }}
            />
          ))}

          {/* Geometric Patterns */}
          <motion.div
            style={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              top: '20%',
              right: '10%',
              background: 'linear-gradient(45deg, rgba(0, 122, 255, 0.03) 25%, transparent 25%), linear-gradient(-45deg, rgba(0, 122, 255, 0.03) 25%, transparent 25%)',
              backgroundSize: '20px 20px',
              borderRadius: '50%',
            }}
            animate={{
              rotate: [0, 360, 720, 1080],
              scale: [1, 1.1, 1, 0.9, 1],
              x: [0, 40, -20, 30, 0],
              y: [0, -30, 50, -10, 0],
              backgroundSize: ["20px 20px", "30px 30px", "15px 15px", "25px 25px", "20px 20px"],
            }}
            transition={{
              duration: 40,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </FloatingElements>

        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <HeroTitle
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Costruiamo
              <br />
              Qualcosa di Grande
            </HeroTitle>
            
            <HeroSubtitle
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Pronto a trasformare le tue idee in realtà? Mettiti in contatto con il nostro team 
              e discutiamo di come possiamo aiutarti a raggiungere i tuoi obiettivi.
            </HeroSubtitle>
          </motion.div>
        </HeroContent>

        <ScrollIndicator
          onClick={scrollToContent}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          onMouseEnter={() => setCursorVariant("button")}
          onMouseLeave={() => setCursorVariant("default")}
        >
          <span>Contattaci</span>
          <FaChevronDown />
        </ScrollIndicator>
      </HeroSection>

      {/* Contact Section */}
      <ContactSection>
        <ContactContainer>
          <ContactGrid>
            {/* Contact Info */}
            <ContactInfo
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              whileHover={{ 
                scale: 1.02, 
                rotateY: 5,
                transition: { duration: 0.3 }
              }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              onMouseEnter={() => setCursorVariant("link")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              <ContactDetail
                whileHover={{ scale: 1.02, x: 10 }}
                onMouseEnter={() => setCursorVariant("link")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                <ContactIcon color1="#007AFF" color2="#5856D6">
                  <FaEnvelope />
                </ContactIcon>
                <ContactText>
                  <ContactLabel>Email</ContactLabel>
                  <ContactValue>commerciale@d-soft.it</ContactValue>
                </ContactText>
              </ContactDetail>

              <ContactDetail
                whileHover={{ scale: 1.02, x: 10 }}
                onMouseEnter={() => setCursorVariant("link")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                <ContactIcon color1="#30D158" color2="#32D74B">
                  <FaPhone />
                </ContactIcon>
                <ContactText>
                  <ContactLabel>Telefono</ContactLabel>
                  <ContactValue>+39 3791408587</ContactValue>
                </ContactText>
              </ContactDetail>

              <ContactDetail
                whileHover={{ scale: 1.02, x: 10 }}
                onMouseEnter={() => setCursorVariant("link")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                <ContactIcon color1="#FF3B30" color2="#FF9500">
                  <FaMapMarkerAlt />
                </ContactIcon>
                <ContactText>
                  <ContactLabel>Indirizzo</ContactLabel>
                  <ContactValue>Via Vincenzo Foppa 55, Milano</ContactValue>
                </ContactText>
              </ContactDetail>
            </ContactInfo>

            {/* Contact Form */}
            <ContactForm
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              whileHover={{ 
                scale: 1.02, 
                rotateY: -5,
                transition: { duration: 0.3 }
              }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              <FormTitle>Inviaci un Messaggio</FormTitle>
              
              <AnimatePresence>
                {submitStatus && (
                  <StatusMessage
                    type={submitStatus}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                  >
                    {submitStatus === 'success' ? (
                      <>
                        <FaCheck />
                        Messaggio inviato con successo! Ti ricontatteremo presto.
                      </>
                    ) : (
                      <>
                        <FaRocket />
                        Qualcosa è andato storto. Riprova di nuovo.
                      </>
                    )}
                  </StatusMessage>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit}>
                <FormGrid>
                  <FormGroup>
                    <FormInput
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder=" "
                      required
                      whileFocus={{ scale: 1.02 }}
                    />
                    <FormLabel>Nome Completo *</FormLabel>
                  </FormGroup>
                  
                  <FormGroup>
                    <FormInput
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder=" "
                      required
                      whileFocus={{ scale: 1.02 }}
                    />
                    <FormLabel>Indirizzo Email *</FormLabel>
                  </FormGroup>
                </FormGrid>

                <FormGrid>
                  <FormGroup>
                    <FormInput
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder=" "
                      whileFocus={{ scale: 1.02 }}
                    />
                    <FormLabel>Azienda</FormLabel>
                  </FormGroup>
                  
                  <FormGroup>
                    <FormInput
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder=" "
                      whileFocus={{ scale: 1.02 }}
                    />
                    <FormLabel>Numero di Telefono</FormLabel>
                  </FormGroup>
                </FormGrid>

                <FormGroup>
                  <FormInput
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                    whileFocus={{ scale: 1.02 }}
                  />
                  <FormLabel>Oggetto *</FormLabel>
                </FormGroup>

                <FormGroup>
                  <FormTextarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder=" "
                    required
                    whileFocus={{ scale: 1.02 }}
                  />
                  <FormLabel>Messaggio *</FormLabel>
                </FormGroup>

                <SubmitButton
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onMouseEnter={() => setCursorVariant("button")}
                  onMouseLeave={() => setCursorVariant("text")}
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="fa-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      Invia Messaggio
                      <FaPaperPlane />
                    </>
                  )}
                </SubmitButton>
              </form>
            </ContactForm>
          </ContactGrid>
        </ContactContainer>
      </ContactSection>
    </div>
  );
};

export default Contact; 